/**
 * FE label map for the consultation selling review, mirroring
 * `api/src/services/sellingReviewCatalog.js` (docs/specs/consultation-selling-review.md).
 *
 * Short, human English labels for codes that come back from the API. Codes
 * are validated server-side against the catalog; this file just needs to
 * stay in sync when the catalog changes. An unknown/unmapped code falls
 * back to the raw code string so nothing ever renders blank.
 */
import type {
  SellingConcernHandling,
  SellingDecisionOutcome,
  SellingDiscoveryState,
  SellingFit
} from '../store/slices/consultationsSlice';

const MISTAKE_LABELS: Record<string, string> = {
  no_clear_decision: 'No clear decision',
  decision_not_helped: 'Decision not helped',
  // Retired codes, kept so older reviews still read well.
  buying_signal_missed: 'Buying signal missed',
  apologetic_offer: 'Apologetic offer',
  decision_fear_not_surfaced: 'Decision fear not surfaced',
  concern_not_addressed: 'Concern not addressed',
  no_pressure_deflection: '"Not pushing" used as a deflection',
  exit_inside_ask: 'Exit offered inside the ask',
  comparison_not_explored: 'Comparison not explored',
  vague_waitlist: 'Vague waitlist',
  retention_risk_unchecked: 'Retention risk unchecked',
  discovery_gap: 'Discovery gap',
  pressure_or_overpromise: 'Pressure or overpromise',
  other: 'Other'
};

const STRENGTH_LABELS: Record<string, string> = {
  clear_decision_reached: 'Clear decision reached',
  thorough_discovery: 'Thorough discovery',
  honest_no_fit: 'Honest "not a fit"',
  mid_course_fear_answered: 'Mid-course fear answered',
  labeled_client_feeling: 'Labeled client feeling',
  clarifying_question: 'Clarifying question',
  linked_to_client_words: "Linked to client's words",
  real_options_offered: 'Real options offered',
  retention_risk_checked: 'Retention risk checked',
  clear_terms: 'Clear terms'
};

const CONCERN_LABELS: Record<string, string> = {
  mid_course_join: 'Joining mid-course',
  speaking_confidence: 'Speaking confidence',
  being_tested: 'Being tested',
  schedule_attendance: 'Schedule / attendance',
  homework_load: 'Homework load',
  paying_before_sure: 'Paying before being sure',
  price_money: 'Price / money',
  group_size: 'Group size',
  bad_past_experience: 'Bad past experience',
  results_guarantee: 'Results guarantee',
  comparison: 'Comparing options',
  other: 'Other'
};

const WANT_LABELS: Record<string, string> = {
  speaking_fluency: 'Speaking fluency',
  work_career: 'Work / career',
  travel_relocation: 'Travel / relocation',
  reading_content: 'Reading / content',
  learning_style: 'Learning style',
  group_format: 'Group format',
  schedule_time: 'Schedule / time',
  exam_certificate: 'Exam / certificate',
  other: 'Other'
};

const CONSTRAINT_LABELS: Record<string, string> = {
  work_hours: 'Work hours',
  study_load: 'Study load',
  family_care: 'Family care',
  irregular_schedule: 'Irregular schedule',
  timezone_location: 'Timezone / location',
  money_shared_decision: 'Shared money decision',
  age: 'Age',
  health: 'Health',
  other: 'Other'
};

const HABIT_LABELS: Record<string, string> = {
  H1_upfront_agreement: 'Agree upfront that "no" is fine',
  H2_name_the_worry: 'Name the worry yourself',
  H3_label_before_answer: 'Label before answering',
  H4_ask_about_obstacle: 'Ask about the obstacle',
  H5_explore_comparison: 'Explore the comparison',
  H6_check_second_month: 'Check the second month',
  H7_ask_and_wait: 'Ask, and pin down any time needed',
  H8_conditional_waitlist: 'Say who writes and when'
};

const DISCOVERY_ITEM_LABELS: Record<string, string> = {
  goal: 'Goal',
  pastExperience: 'Past experience',
  constraints: 'Constraints',
  decisionCriteria: 'Decision criteria'
};

const DISCOVERY_STATE_LABELS: Record<SellingDiscoveryState, string> = {
  covered: 'Covered',
  partial: 'Partial',
  missing: 'Not clear',
  not_needed: 'Not needed'
};

const FIT_LABELS: Record<SellingFit, string> = {
  fit: 'Group fit',
  partial: 'Partial fit',
  no_group: 'No group available',
  unclear: 'Fit unclear'
};

const DECISION_OUTCOME_LABELS: Record<SellingDecisionOutcome, string> = {
  joining: 'Joining',
  declined: 'Declined',
  waiting_for_group: 'Waiting for a group',
  thinking_with_plan: 'Thinking it over',
  unclear: 'No clear decision'
};

const HANDLING_LABELS: Record<SellingConcernHandling, string> = {
  handled: 'Handled',
  badly_answered: 'Badly answered',
  unanswered: 'Unanswered'
};

function lookup(map: Record<string, string>, code: string): string {
  return map[code] || code;
}

export const mistakeLabel = (code: string): string => lookup(MISTAKE_LABELS, code);
export const strengthLabel = (code: string): string => lookup(STRENGTH_LABELS, code);
export const concernLabel = (code: string): string => lookup(CONCERN_LABELS, code);
export const wantLabel = (code: string): string => lookup(WANT_LABELS, code);
export const constraintLabel = (code: string): string => lookup(CONSTRAINT_LABELS, code);
export const habitLabel = (code: string): string => lookup(HABIT_LABELS, code);
export const discoveryItemLabel = (code: string): string => lookup(DISCOVERY_ITEM_LABELS, code);

export const discoveryStateLabel = (state: SellingDiscoveryState): string =>
  DISCOVERY_STATE_LABELS[state] || state;

export const fitLabel = (fit: SellingFit): string => FIT_LABELS[fit] || fit;

export const decisionOutcomeLabel = (outcome: SellingDecisionOutcome): string =>
  DECISION_OUTCOME_LABELS[outcome] || outcome;

export const handlingLabel = (handling: SellingConcernHandling): string =>
  HANDLING_LABELS[handling] || handling;

/** Label for a finding of any kind, dispatching on `kind`. */
export function findingLabel(kind: string, code: string): string {
  switch (kind) {
    case 'mistake': return mistakeLabel(code);
    case 'strength': return strengthLabel(code);
    case 'concern': return concernLabel(code);
    case 'want': return wantLabel(code);
    case 'constraint': return constraintLabel(code);
    default: return code;
  }
}
